/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  CurrentItemVariant,
  ListItemRendererContext,
  SelectionDetail
} from '#UNSAFE_Collection';
import type { ReorderableItemProps } from '#UNSAFE_Collection/Reorderable.types';
import type { ItemPadding } from './List.types';

import { useCallback, useRef } from 'preact/hooks';
import { memo } from 'preact/compat';
import { classNames } from '#utils/UNSAFE_classNames';
import { isKeyDefined, isSameKey } from '#utils/UNSAFE_keys';
import { useReorderableItem } from '#hooks/PRIVATE_useReorderable';
import { LIST_ROW_SELECTOR } from './utils/ListDomUtils';
import { reorderableStyles } from './themes/List.css';
import { ListRow } from './ListRow';

/**
 * Props for the ListRow Component
 */
type ListReorderableRowProps<K extends string | number, D> = {
  listId: string;
  rowKey: K;
  rowData: D;
  rowIndex: number;
  isFinalRow: boolean;
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  padding: ItemPadding;
  hasTopGridline: boolean;
  hasBottomGridline: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isSelectionEnabled: boolean;
  isSingleSelectionEnabled: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  selectedRowKeys: Keys<K>;
  isShowFocusRing: boolean;
  isActive: boolean;
  isTabbableMode: boolean;
  isSuggestion: boolean;
  variant?: CurrentItemVariant;
  onSelectionChange?: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void;
  disabledInteractionKeys?: ('enter' | 'space')[];
} & ReorderableItemProps<K>;

/**
 * The internal component used to render a single reorderable row in List.
 */
function ListReorderableRow<K extends string | number, D>({
  children,
  dragKey,
  setDragKey,
  rootRef,
  onReorder,
  ...rowProps
}: ListReorderableRowProps<K, D>) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const itemStyles = {
    itemOverlayFront: reorderableStyles.reorderableItemOverlayFront,
    itemOverlayRear: reorderableStyles.reorderableItemOverlayRear,
    itemDragger: reorderableStyles.reorderableItemDragger
  };
  const itemSelector = `[${LIST_ROW_SELECTOR}='${rowProps.listId}']`;
  const getItem = () => itemRef?.current?.closest(itemSelector);
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/listview-items-key', JSON.stringify(rowProps.rowKey));
    },
    [rowProps.rowKey]
  );

  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      isDisabled: onReorder ? false : true,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: itemRef,
      getItem,
      setTransferData,
      itemStyles,
      itemSelector,
      isVertical: true
    });

  const reorderClassArray = [];
  if (rootProps?.class != null) {
    reorderClassArray.push(rootProps.class);
  }
  reorderClassArray.push(reorderableStyles.reorderableItemDnd);
  const reorderClasses = classNames(reorderClassArray);

  const reorderableRenderer = (context: ListItemRendererContext<K, D>) => {
    return (
      <div {...rootProps} class={reorderClasses}>
        {children(context)}
        {!isDragged && isKeyDefined(dragKey) && (
          <>
            <div {...overlayZoneBeforeProps}></div>
            <div {...overlayZoneAfterProps}></div>
          </>
        )}
      </div>
    );
  };

  return <ListRow {...rowProps}>{reorderableRenderer}</ListRow>;
}

export const MemoizeListReorderableRow = memo(
  ListReorderableRow,
  (
    prev: ListReorderableRowProps<string | number, unknown>,
    next: ListReorderableRowProps<string | number, unknown>
  ) => {
    if (prev && next) {
      return (
        prev.listId === next.listId &&
        prev.rowKey === next.rowKey &&
        prev.rowData === next.rowData &&
        prev.rowIndex === next.rowIndex &&
        prev.isFinalRow === next.isFinalRow &&
        prev.children === next.children &&
        prev.padding === next.padding &&
        prev.hasTopGridline === next.hasTopGridline &&
        prev.hasBottomGridline === next.hasBottomGridline &&
        prev.isSelected === next.isSelected &&
        prev.isPreviousSelected === next.isPreviousSelected &&
        prev.isSelectionEnabled === next.isSelectionEnabled &&
        prev.isSingleSelectionEnabled === next.isSingleSelectionEnabled &&
        (prev.isSelectionEnabled && !prev.isSingleSelectionEnabled
          ? isSameKey(prev.selectedRowKeys, next.selectedRowKeys)
          : true) &&
        prev.isShowFocusRing === next.isShowFocusRing &&
        prev.isActive === next.isActive &&
        prev.isTabbableMode === next.isTabbableMode &&
        prev.isSuggestion === next.isSuggestion &&
        prev.variant === next.variant &&
        prev.onSelectionChange === next.onSelectionChange &&
        prev.isRtl === next.isRtl &&
        prev.isHighContrast === next.isHighContrast &&
        prev.disabledInteractionKeys === next.disabledInteractionKeys &&
        prev.dragKey === next.dragKey &&
        prev.setDragKey === next.setDragKey &&
        prev.onReorder === next.onReorder &&
        prev.rootRef === next.rootRef
      );
    }
    return false;
  }
);
